import React, { useEffect, useState } from 'react';
import { connectionsAPI } from '../../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const intervalLabel = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};
const REGIONS = [
  { value: 'england-and-wales', label: 'England and Wales' },
  { value: 'scotland', label: 'Scotland' },
  { value: 'northern-ireland', label: 'Northern Ireland' },
  { value: 'none', label: 'None: every weekday is a working day' },
];

/** A list of tick boxes over the leave policies. */
function Checks({ legend, hint, names, chosen, onToggle, label }) {
  return (
    <fieldset className="connection-field">
      <legend>{legend}</legend>
      {hint && <p className="source-hint">{hint}</p>}
      <div className="connection-checks">
        {names.length === 0 && <span className="source-hint">No leave policies found in Sage HR.</span>}
        {names.map((n) => (
          <label key={n}>
            <input type="checkbox" aria-label={`${label} ${n}`} checked={chosen.includes(n)} onChange={() => onToggle(n)} />
            {n}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Which leave policies count as holiday and as sickness, which bank holidays
 * are not working days, and how much history to read.
 */
export default function SageHrSettings({ connector, connection, companyId, onSaved }) {
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    connectionsAPI.options(connector.id, companyId)
      .then((res) => {
        if (cancelled) return;
        const s = connection.definitions || {};
        setOptions(res.data);
        setDraft({
          interval: connection.syncIntervalMinutes,
          holidayPolicies: s.holidayPolicies || [],
          sicknessPolicies: s.sicknessPolicies || [],
          bankHolidays: s.bankHolidays || 'england-and-wales',
          historyMonths: s.historyMonths,
        });
      })
      .catch((err) => { if (!cancelled) setError(describeError(err, 'Could not read the options from Sage HR')); });
    return () => { cancelled = true; };
    // Options are read once when the settings open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id, companyId]);

  if (error && !options) return <div className="error-message" role="alert">{error}</div>;
  if (!options || !draft) return <p className="source-hint">Reading the options from Sage HR…</p>;

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };
  const toggle = (name) => (value) => set({ [name]: draft[name].includes(value) ? draft[name].filter((v) => v !== value) : [...draft[name], value] });

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: {
          holidayPolicies: draft.holidayPolicies,
          sicknessPolicies: draft.sicknessPolicies,
          bankHolidays: draft.bankHolidays,
          historyMonths: Number(draft.historyMonths),
        },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{intervalLabel(m)}</option>)}
        </select>
      </label>

      <Checks legend="Leave policies that mean sickness" label="Sickness policy"
        hint="Absence rate counts these. With nothing ticked, policies with sick in the name count."
        names={options.policies} chosen={draft.sicknessPolicies} onToggle={toggle('sicknessPolicies')} />
      <Checks legend="Leave policies that mean holiday" label="Holiday policy"
        hint="With nothing ticked, policies with holiday, annual leave or vacation in the name count. Any other policy counts as other leave."
        names={options.policies} chosen={draft.holidayPolicies} onToggle={toggle('holidayPolicies')} />

      <label className="connection-field">
        <span>Bank holidays</span>
        <select value={draft.bankHolidays} onChange={(e) => set({ bankHolidays: e.target.value })}>
          {REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </label>
      <p className="source-hint">Bank holidays and weekends are not working days. The dates come from GOV.UK.</p>

      <label className="connection-field">
        <span>Months of history</span>
        <input type="number" min="1" max="120" step="1" value={draft.historyMonths} onChange={(e) => set({ historyMonths: e.target.value })} />
      </label>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. The next sync uses these settings.</span>}
      </div>
    </form>
  );
}
